import { io, Socket } from 'socket.io-client';
import { logger } from '@/utils/logger';

export interface NotificationMessage {
  id: string;
  type: string;
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  metadata?: any;
  timestamp: string;
  read: boolean;
}

export interface WebSocketEventHandlers {
  onNotification?: (notification: NotificationMessage) => void;
  onConnectionChange?: (connected: boolean) => void;
  onError?: (error: Error) => void;
}

export class WebSocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private eventHandlers: WebSocketEventHandlers = {};
  private pendingRoomJoins: Set<string> = new Set();
  // Generic event listeners registered via `.on(event, handler)`.
  // Stored even before the socket exists so listeners survive
  // reconnects: setupEventListeners() walks this map on each
  // socket bind. eslint-disable: payload shape is event-specific.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private pendingListeners: Map<string, Set<(payload: any) => void>> = new Map();

  constructor() {
    this.connect = this.connect.bind(this);
    this.disconnect = this.disconnect.bind(this);
    this.sendMessage = this.sendMessage.bind(this);
  }

  /**
   * Get WebSocket URL based on environment
   */
  private getWebSocketUrl(): string {
    // Use explicit env var if set
    if (import.meta.env.VITE_WS_URL) {
      return import.meta.env.VITE_WS_URL;
    }

    // In production, derive from current location
    // WebSocket connects to user-service which handles /socket.io
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;

    // In development, use localhost:3001 (user-service port)
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'ws://localhost:3001';
    }

    // In production, WebSocket goes through the same host (nginx routes /socket.io to user-service)
    return `${protocol}//${host}`;
  }

  /**
   * Connect to WebSocket server
   */
  connect(token: string): void {
    if (this.socket?.connected) {
      logger.info('WebSocket already connected');
      return;
    }

    // #643: tear down any prior socket before creating a new one. The old
    // path created a `forceNew` socket on every (re)connect without cleaning
    // up the previous one — orphaned sockets kept their own reconnection loop
    // running, and combined with the custom attemptReconnect() this leaked
    // connections until the browser hit ERR_INSUFFICIENT_RESOURCES, which then
    // dropped unrelated requests like image uploads.
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    const wsUrl = this.getWebSocketUrl();

    logger.info('Connecting to WebSocket server', { url: wsUrl });

    // #643: a single socket.io instance using its built-in reconnection only —
    // no `forceNew` (which created a brand-new socket on every (re)connect and,
    // together with the old custom reconnect loop, leaked connections until the
    // browser hit ERR_INSUFFICIENT_RESOURCES and dropped unrelated requests like
    // image uploads). The connect() teardown above guarantees a single live
    // socket; this is the only reconnection mechanism (bounded attempts, with a
    // reconnect_failed handler below to surface exhaustion after a long outage).
    this.socket = io(wsUrl, {
      auth: {
        token: token,
      },
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000
    });

    this.setupEventListeners();
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      logger.info('Disconnecting from WebSocket server');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.pendingRoomJoins.clear();
    }
  }

  /**
   * Set event handlers
   */
  setEventHandlers(handlers: WebSocketEventHandlers): void {
    this.eventHandlers = { ...this.eventHandlers, ...handlers };
  }

  /**
   * Send message to server
   */
  sendMessage(event: string, data: any): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      logger.warn('Cannot send message: WebSocket not connected');
    }
  }

  /**
   * Join a room/channel
   */
  joinRoom(room: string): void {
    if (this.socket?.connected) {
      this.socket.emit('join_room', room);
      logger.info(`Joined room: ${room}`);
    } else {
      // Queue the room join for when connection is established
      this.pendingRoomJoins.add(room);
      logger.debug(`Queued room join: ${room} (waiting for connection)`);
    }
  }

  /**
   * Leave a room/channel
   */
  leaveRoom(room: string): void {
    if (this.socket?.connected) {
      this.socket.emit('leave_room', room);
      logger.info(`Left room: ${room}`);
    } else {
      logger.warn('Cannot leave room: WebSocket not connected');
    }
  }

  /**
   * Get connection status
   */
  getConnectionStatus(): boolean {
    return this.isConnected;
  }

  /**
   * Register a generic event listener. Used by feature hooks
   * (e.g. useMessagingWs) that need to subscribe to socket.io
   * events the singleton WebSocketContext doesn't know about
   * (message:new, message:deleted, message:read, …). Mirrors
   * socket.io's `on`/`off` so callers can reuse the same handler
   * reference for both.
   *
   * Survives reconnects: handlers registered before the socket
   * connects are queued and attached when setupEventListeners runs.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on(event: string, handler: (payload: any) => void): void {
    // Stash so reconnect can re-bind.
    if (!this.pendingListeners.has(event)) {
      this.pendingListeners.set(event, new Set());
    }
    this.pendingListeners.get(event)!.add(handler);
    if (this.socket) {
      this.socket.on(event, handler);
    }
  }

  /**
   * Unregister a previously-registered event listener.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  off(event: string, handler: (payload: any) => void): void {
    this.pendingListeners.get(event)?.delete(handler);
    if (this.socket) {
      this.socket.off(event, handler);
    }
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      logger.info('WebSocket connected');
      this.isConnected = true;

      // Replay any room joins that were queued while disconnected.
      // This is what makes deploys transparent — the page-level
      // joinRoom() calls survive a disconnect and rejoin
      // automatically when the socket comes back.
      if (this.pendingRoomJoins.size > 0) {
        logger.info(`Processing ${this.pendingRoomJoins.size} pending room joins`);
        this.pendingRoomJoins.forEach((room) => {
          this.socket?.emit('join_room', room);
          logger.info(`Joined queued room: ${room}`);
        });
        this.pendingRoomJoins.clear();
      }

      this.eventHandlers.onConnectionChange?.(true);
    });

    this.socket.on('disconnect', (reason) => {
      logger.warn('WebSocket disconnected', { reason });
      this.isConnected = false;
      this.eventHandlers.onConnectionChange?.(false);
      // #643: socket.io auto-reconnects for transport drops, but NOT when the
      // server explicitly disconnects us. Reconnect the SAME socket in place
      // (no new io()/forceNew, so no orphaned-socket leak).
      if (reason === 'io server disconnect') {
        this.socket?.connect();
      }
    });

    this.socket.on('connect_error', (error) => {
      logger.warn('WebSocket connection error', { error: error.message });
      this.isConnected = false;
      this.eventHandlers.onConnectionChange?.(false);
      this.eventHandlers.onError?.(error);
      // #643: an auth failure won't fix itself on retry — stop socket.io's
      // built-in reconnection so it doesn't loop on a bad token. Transient
      // errors are left to the built-in (bounded) reconnection; no manual loop.
      // (Keep staging's broader match so 'jwt …' errors are treated as auth too.)
      if (error.message.includes('Authentication') || error.message.includes('jwt')) {
        logger.error('WebSocket authentication failed; stopping reconnection');
        this.socket?.disconnect();
      }
    });

    // #643: built-in reconnection exhausted its bounded attempts — log and
    // surface, do not spawn another connection.
    this.socket.on('reconnect_failed', () => {
      logger.error('Max reconnection attempts reached');
      this.isConnected = false;
      this.eventHandlers.onConnectionChange?.(false);
    });

    this.socket.on('notification', (notification: NotificationMessage) => {
      logger.info('Received notification', { notification });
      this.eventHandlers.onNotification?.(notification);
    });

    this.socket.on('error', (error) => {
      logger.error('WebSocket error', { error });
      this.eventHandlers.onError?.(error);
    });

    // Handle specific notification types
    this.socket.on('commission_earned', (data) => {
      logger.info('Commission earned notification', { data });
      this.eventHandlers.onNotification?.({
        id: data.id || `commission_${Date.now()}`,
        type: 'commission_earned',
        title: 'Commission Earned',
        message: `You earned $${data.amount} commission from ${data.hotel_name}`,
        priority: 'medium',
        metadata: data,
        timestamp: new Date().toISOString(),
        read: false
      });
    });

    this.socket.on('payout_processed', (data) => {
      logger.info('Payout processed notification', { data });
      this.eventHandlers.onNotification?.({
        id: data.id || `payout_${Date.now()}`,
        type: 'payout_processed',
        title: 'Payout Processed',
        message: `Your payout of $${data.amount} has been processed`,
        priority: 'high',
        metadata: data,
        timestamp: new Date().toISOString(),
        read: false
      });
    });

    this.socket.on('partnership_approved', (data) => {
      logger.info('Partnership approved notification', { data });
      this.eventHandlers.onNotification?.({
        id: data.id || `partnership_${Date.now()}`,
        type: 'partnership_approved',
        title: 'Partnership Approved',
        message: `Your partnership with ${data.hotel_name} has been approved`,
        priority: 'high',
        metadata: data,
        timestamp: new Date().toISOString(),
        read: false
      });
    });

    this.socket.on('verification_required', (data) => {
      logger.info('Verification required notification', { data });
      this.eventHandlers.onNotification?.({
        id: data.id || `verification_${Date.now()}`,
        type: 'verification_required',
        title: 'Verification Required',
        message: 'Please complete your account verification',
        priority: 'high',
        metadata: data,
        timestamp: new Date().toISOString(),
        read: false
      });
    });

    // Re-bind every generic listener registered via `.on()` before
    // (or after) connect. Same handler reference is reused, so this
    // is safe on reconnect — socket.io's listener array is keyed
    // per-socket and the previous socket is gone.
    this.pendingListeners.forEach((handlers, event) => {
      handlers.forEach((handler) => {
        this.socket?.on(event, handler);
      });
    });
  }

}

// Create singleton instance
export const websocketService = new WebSocketService();
